import { ViewShift } from '@xeetr/types/models'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import ShiftContent from './ShiftContent'

import { ShiftFormProps } from '@/types/ShiftForm'

/**
 * Individual shift form with independent FormProvider
 * Each accordion item is its own form
 */
const ShiftForm = ({
  shift,
  isExpanded,
  onRegisterForm,
  onUpdate,
  onCopy,
  onDelete,
}: ShiftFormProps) => {
  const methods = useForm<ViewShift>({
    defaultValues: shift,
    mode: 'onChange', // Validate on change for immediate feedback
  })

  const { formState, handleSubmit, reset } = methods

  // Register/unregister form control when expanded state changes
  useEffect(() => {
    if (isExpanded) {
      const formHandle = {
        isDirty: formState.isDirty,
        isSaving: formState.isSubmitting,
        shiftId: shift.id,
        onSave: handleSubmit((data) => {
          onUpdate(data)
          // Reset form with new data to mark as clean
          reset(data)
        }),
        onDiscard: () => {
          reset(shift) // Reset to original shift data
        },
      }
      onRegisterForm(formHandle)
    } else {
      onRegisterForm(null)
    }
  }, [isExpanded, formState.isDirty, formState.isSubmitting, shift.id])

  // Update form control when dirty state changes (user types)
  useEffect(() => {
    if (isExpanded) {
      const formHandle = {
        isDirty: formState.isDirty,
        isSaving: formState.isSubmitting,
        shiftId: shift.id,
        onSave: handleSubmit((data) => {
          onUpdate(data)
          reset(data)
        }),
        onDiscard: () => {
          reset(shift)
        },
      }
      onRegisterForm(formHandle)
    }
  }, [formState.isDirty, formState.isSubmitting])

  // Sync form with external shift changes (e.g., after successful save)
  useEffect(() => {
    reset(shift)
  }, [shift])

  return (
    <FormProvider {...methods}>
      <form onSubmit={(e) => e.preventDefault()}>
        <ShiftContent onCopy={onCopy} onDelete={onDelete} />
      </form>
    </FormProvider>
  )
}

export default ShiftForm
